import { CSSProperties, FC, MouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Col, Input, InputNumber, Menu, Row, Segmented } from 'antd';
import { TbCheck } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { DropdownMenuPanel, FilterButtonGroup, LearnMoreFooter } from '@/shared';
import { FinancialCompanySelect, PlatformDatePicker, PlatformSelect as Select } from '@/shared/components/forms';
import { ListViewColumnDataType } from '@/types';
import { ellipsisStyling } from '@/utilities';
import dayjs, { serializeDatePickerValues } from '@/utilities/dayjs';

import {
    canReuseListFilterValue,
    dateDataTypes,
    FilterableColumn,
    formatListFilterValue,
    getColumnOperatorOptions,
    getInputIcon,
    hasListFilterValue,
    ListFilterOperator,
    ListFilterRule,
    ListFilterValue,
    ListFilterValueJoin,
    normalizeListFilterValues,
    numericDataTypes,
    toStoredListFilterValue,
} from '../../01-FilterDropdown';
import FilterValueSelect from '../../01-FilterDropdown/components/FilterValueSelect';

interface FilterButtonItemProps {
    columns?: FilterableColumn[];
    docsLabel?: string;
    filter: ListFilterRule;
    filters: ListFilterRule[];
    setFilters: (next: ListFilterRule[] | null) => void;
}

const FilterButtonItem: FC<FilterButtonItemProps> = ({
    columns,
    docsLabel = 'List filters',
    filter,
    filters,
    setFilters,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { listColumnKeyToIconMap } = useMaps();
    const [valueOpen, setValueOpen] = useState(false);
    const [draftValue, setDraftValue] = useState<ListFilterValue>(filter.value ?? '');
    const [draftValueJoin, setDraftValueJoin] = useState<ListFilterValueJoin>(filter.valueJoin ?? 'and');

    const visibleColumns = useMemo(
        () => (columns ?? []).filter((column) => !column.isHidden).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [columns],
    );

    const activeColumn = visibleColumns.find((column) => column.key === filter.field);
    const operatorOptions = getColumnOperatorOptions(activeColumn ?? { dataType: filter.dataType });

    useEffect(() => {
        if (valueOpen) {
            setDraftValue(filter.value ?? '');
            setDraftValueJoin(filter.valueJoin ?? 'and');
        }
    }, [filter.value, filter.valueJoin, valueOpen]);

    const updateFilter = (updates: Partial<ListFilterRule>) => {
        const nextRules = filters.map((rule) => (rule.id === filter.id ? { ...rule, ...updates } : rule));

        setFilters(nextRules.length ? nextRules : null);
    };

    const removeFilter = () => {
        const nextRules = filters.filter((rule) => rule.id !== filter.id);

        setFilters(nextRules.length ? nextRules : null);
    };

    const renderTertiaryIcon = (icon: ReactNode) => (
        <span
            style={{
                alignItems: 'center',
                color: token.colorTextTertiary,
                display: 'inline-flex',
                flex: 'none',
                lineHeight: 0,
            }}
        >
            {icon}
        </span>
    );

    const columnIconFor = (column?: FilterableColumn) =>
        renderTertiaryIcon(
            column
                ? (column.icon ?? listColumnKeyToIconMap[column.key] ?? getInputIcon(column.dataType))
                : getInputIcon(filter.dataType),
        );

    const buttonStyle = {
        alignItems: 'center',
        display: 'inline-flex',
        justifyContent: 'flex-start',
        maxWidth: 16 * 14,
        minWidth: 0,
        overflow: 'hidden',
        textAlign: 'left',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as CSSProperties;

    const filterButtonContentStyle = {
        alignItems: 'center',
        color: token.colorTextSecondary,
        display: 'inline-flex',
        fontSize: token.fontSize,
        fontWeight: 400,
        gap: '0.375rem',
        lineHeight: '1rem',
        maxWidth: '100%',
        minWidth: 0,
    } as CSSProperties;

    const filterButtonLabelStyle = {
        ...ellipsisStyling,
        color: 'inherit',
        alignItems: 'center',
        display: 'inline-flex',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        minWidth: 0,
    } as CSSProperties;

    const renderFilterLabel = (message: ReactNode) => (
        <span
            style={{
                ...filterButtonLabelStyle,
                alignItems: 'center',
                color: token.colorTextSecondary,
                display: 'inline-flex',
                maxWidth: '100%',
            }}
        >
            {message}
        </span>
    );

    const commitValue = (nextValue: ListFilterValue = draftValue) => {
        updateFilter({
            value: toStoredListFilterValue(filter.dataType, nextValue),
            valueJoin: draftValueJoin,
        });
        setValueOpen(false);
    };

    const stopDropdownPropagation = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    const docsFooter = <LearnMoreFooter topic={docsLabel} variant='dropdown' />;

    const renderValueEditor = () => {
        if (activeColumn?.filterValueEditor === 'company') {
            return (
                <FinancialCompanySelect
                    multiple={activeColumn.allowMultipleValues}
                    onChange={setDraftValue}
                    value={draftValue as string | string[]}
                />
            );
        }

        if (activeColumn?.filterOptions?.length) {
            const multiple = activeColumn.allowMultipleValues !== false;
            const normalizedValue = normalizeListFilterValues(draftValue);

            return (
                <FilterValueSelect
                    fallbackIcon={columnIconFor(activeColumn)}
                    fieldLabel={activeColumn.label}
                    multiple={multiple}
                    value={multiple ? normalizedValue : normalizedValue[0]}
                    onChange={setDraftValue}
                    options={activeColumn.filterOptions}
                    placeholder='Select Values...'
                />
            );
        }

        if (filter.dataType === ListViewColumnDataType.BOOLEAN) {
            const multiple = activeColumn?.allowMultipleValues === true;
            const normalizedValue = normalizeListFilterValues(draftValue).filter(
                (item): item is boolean => typeof item === 'boolean',
            );

            return (
                <Select
                    autoFocus
                    mode={multiple ? 'multiple' : undefined}
                    value={multiple ? normalizedValue : normalizedValue[0]}
                    onChange={setDraftValue}
                    options={[
                        { label: 'True', value: true },
                        { label: 'False', value: false },
                    ]}
                    placeholder='Select Value...'
                    style={{ width: '100%' }}
                />
            );
        }

        if (filter.dataType && numericDataTypes.has(filter.dataType)) {
            return activeColumn?.allowMultipleValues ? (
                <Select
                    autoFocus
                    mode='tags'
                    value={normalizeListFilterValues(draftValue).map(String)}
                    onChange={setDraftValue}
                    onInputKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            commitValue();
                        }
                    }}
                    placeholder='Enter Values...'
                    style={{ width: '100%' }}
                    tokenSeparators={[',']}
                />
            ) : (
                <InputNumber
                    autoFocus
                    onChange={(nextValue) => setDraftValue(nextValue ?? '')}
                    onPressEnter={() => commitValue()}
                    placeholder='Enter A Value...'
                    style={{ width: '100%' }}
                    value={typeof draftValue === 'number' ? draftValue : Number(draftValue) || undefined}
                />
            );
        }

        if (filter.dataType && dateDataTypes.has(filter.dataType)) {
            return (
                <PlatformDatePicker
                    autoFocus
                    multiple
                    value={normalizeListFilterValues(draftValue)
                        .map((item) => dayjs(String(item)))
                        .filter((item) => item.isValid())}
                    onChange={(dates) => setDraftValue(serializeDatePickerValues(dates))}
                    placeholder='Select Date...'
                    style={{ width: '100%' }}
                />
            );
        }

        return activeColumn?.allowMultipleValues ? (
            <Select
                autoFocus
                mode='tags'
                value={normalizeListFilterValues(draftValue).map(String)}
                onChange={setDraftValue}
                onInputKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        commitValue();
                    }
                }}
                placeholder='Enter Values...'
                style={{ width: '100%' }}
                tokenSeparators={[',']}
            />
        ) : (
            <Input
                autoFocus
                maxLength={500}
                onChange={(event) => setDraftValue(event.target.value)}
                onPressEnter={() => commitValue()}
                placeholder='Enter A Value...'
                showCount
                value={typeof draftValue === 'string' ? draftValue : String(draftValue)}
            />
        );
    };

    const fieldOverlay = (
        <DropdownMenuPanel
            selectable={false}
            style={{ width: 16 * 18, padding: 0 }}
            onClick={({ key }) => {
                const nextColumn = visibleColumns.find((column) => column.key === key);

                if (!nextColumn) {
                    return;
                }

                const nextOperatorOptions = getColumnOperatorOptions(nextColumn);
                const currentOperatorIsValid = nextOperatorOptions.some((option) => option.value === filter.operator);
                const [nextOperator] = nextOperatorOptions;
                const canReuseValue = canReuseListFilterValue(filter.dataType, nextColumn.dataType);

                updateFilter({
                    dataType: nextColumn.dataType,
                    field: nextColumn.key,
                    fieldLabel: nextColumn.label,
                    operator: currentOperatorIsValid ? filter.operator : (nextOperator?.value ?? 'contains'),
                    operatorLabel: currentOperatorIsValid ? filter.operatorLabel : (nextOperator?.label ?? 'Contains'),
                    value: canReuseValue ? filter.value : '',
                });
            }}
        >
            <small
                style={{
                    color: token.colorTextTertiary,
                    display: 'block',
                    padding: '0.75rem 0.75rem 0.375rem',
                }}
            >
                Filter Field
            </small>
            <div style={{ maxHeight: 16 * 18, overflowY: 'auto' }}>
                {visibleColumns.map((column) => {
                    const isActive = column.key === filter.field;

                    return (
                        <Menu.Item
                            key={column.key}
                            icon={
                                <Avatar
                                    shape='square'
                                    size={16}
                                    icon={columnIconFor(column)}
                                    style={{ border: `1px solid ${token.colorBorder}` }}
                                />
                            }
                            style={{
                                background: isActive ? token.controlItemBgActive : 'transparent',
                                color: isActive ? token.colorPrimary : token.colorText,
                            }}
                        >
                            <Row align='middle' style={{ width: '100%' }} wrap={false}>
                                <Col flex='auto'>{column.label}</Col>
                                {isActive && (
                                    <Col flex='none'>
                                        <TbCheck size='0.875rem' style={{ color: token.colorPrimary }} />
                                    </Col>
                                )}
                            </Row>
                        </Menu.Item>
                    );
                })}
            </div>
            {docsFooter}
        </DropdownMenuPanel>
    );

    const operatorOverlay = (
        <DropdownMenuPanel
            selectedKeys={[filter.operator]}
            style={{ width: 16 * 14, padding: 0 }}
            onClick={({ key }) => {
                const nextOperator = operatorOptions.find((option) => option.value === key);

                if (!nextOperator) {
                    return;
                }

                updateFilter({
                    operator: nextOperator.value as ListFilterOperator,
                    operatorLabel: nextOperator.label,
                });
            }}
        >
            <small
                style={{
                    color: token.colorTextTertiary,
                    display: 'block',
                    padding: '0.75rem 0.75rem 0.375rem',
                }}
            >
                Filter Operator
            </small>
            {operatorOptions.map((option) => (
                <Menu.Item key={option.value}>{option.label}</Menu.Item>
            ))}
            {docsFooter}
        </DropdownMenuPanel>
    );

    const valueOverlay = (
        <DropdownMenuPanel style={{ width: 16 * 18, padding: 0 }}>
            <div onClick={stopDropdownPropagation} style={{ padding: '0.75rem' }}>
                <small style={{ color: token.colorTextTertiary, display: 'block', marginBottom: '0.25rem' }}>
                    Filter Value
                </small>
                <div style={{ color: token.colorText, fontWeight: 500, marginBottom: '0.5rem' }}>
                    {filter.fieldLabel}
                </div>
                {renderValueEditor()}
                <Row align='middle' style={{ marginTop: '0.5rem' }}>
                    <Col flex='none'>
                        <Segmented
                            options={[
                                { label: 'AND', value: 'and' },
                                { label: 'OR', value: 'or' },
                            ]}
                            onChange={(nextValue) => setDraftValueJoin(nextValue as ListFilterValueJoin)}
                            size='small'
                            value={draftValueJoin}
                        />
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <Button
                            disabled={!hasListFilterValue(toStoredListFilterValue(filter.dataType, draftValue))}
                            size='small'
                            type='primary'
                            onClick={() => commitValue()}
                        >
                            Apply
                        </Button>
                    </Col>
                </Row>
            </div>
            {docsFooter}
        </DropdownMenuPanel>
    );

    const formattedValue = formatListFilterValue(filter.value);

    return (
        <FilterButtonGroup
            onRemove={removeFilter}
            removeTooltip='Remove Filter'
            segments={[
                {
                    dropdown: fieldOverlay,
                    key: 'field',
                    label: (
                        <span style={filterButtonContentStyle}>
                            <span style={{ alignItems: 'center', display: 'inline-flex', flex: '0 0 auto' }}>
                                {columnIconFor(activeColumn)}
                            </span>
                            {renderFilterLabel(filter.fieldLabel)}
                        </span>
                    ),
                    maxWidth: 16 * 12,
                    style: buttonStyle,
                },
                {
                    dropdown: operatorOverlay,
                    key: 'operator',
                    label: <span style={filterButtonContentStyle}>{renderFilterLabel(filter.operatorLabel)}</span>,
                    maxWidth: 16 * 11,
                    style: buttonStyle,
                },
                {
                    dropdown: valueOverlay,
                    key: 'value',
                    label: <span style={filterButtonContentStyle}>{renderFilterLabel(formattedValue)}</span>,
                    maxWidth: 16 * 12,
                    onDropdownOpenChange: setValueOpen,
                    open: valueOpen,
                    style: buttonStyle,
                },
            ]}
            style={{ marginRight: '0.375rem' }}
        />
    );
};

export default FilterButtonItem;
